import cloneDeep from 'lodash-es/cloneDeep';
import DOMPurify from 'dompurify';
import { QuestionGroup } from './../../models/question.model';
import { ObjectUrlService } from './../object-url/object-url.service';
import { AlertService, AlertOption } from '../alert/alert.service';
import {
  Questionnaire,
  EngagementQuestion,
  EngagementOption,
  EngagementValidation,
  DataPoint,
  QuestionAnswer,
  DataPointOption,
} from '../../models/engagement.model';
import { Question, Choice, ValidationRule, MessageStatus, QuestionTypes, ValidationType } from '../../models/question.model';
import { Injectable } from '@angular/core';
import { LoadingController, Platform } from '@ionic/angular';
import { NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConnectService } from '../connect/connect.service';
import * as dayjs from 'dayjs';
import { LogicParserService } from '../logic-parser/logic-parser.service';
import { PhotoVideoService } from '../photo-video/photo-video.service';
import { Result } from 'src/app/models/result.model';
import { SentryService, SentrySeverityLevel } from '../sentry/sentry.service';
import { EventBusService, EventTopic } from '../event-bus/event-bus.service';
import { isPhotoVideoQuestionType, isPhotoVideoResultUploaded } from 'src/app/utilities/question';
import { HappitechResults } from 'src/app/pages/happitech/happitech-plugin.service';
import { from } from 'rxjs';
import { StorageService } from '../storage/storage.service';

export const DEFAULT_VIDEO_THUMBNAIL = '/assets/images/default-video-thumbnail.svg';
const EXCLUDED_GROUPED_QUESTIONS_TYPE = [
  QuestionTypes.HappyTechQuestion,
  QuestionTypes.PhotoQuestion,
  QuestionTypes.VideoQuestion,
  QuestionTypes.RangeSliderQuestion,
];
export interface File {
  name: string;
  type: string;
  size?: number;
  fullPath: string;
  logo?: string;
}

export interface VideoFile extends File {
  width?: number;
  height?: number;
}

export interface QuestionFiles {
  video: VideoFile;
  photo: File;
}
export interface ValidationMessage {
  message: string;
  status: MessageStatus;
}

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService {
  files = {} as QuestionFiles;
  title = '';
  questionnaire: Question[] = [];
  questionGroups: QuestionGroup[] = [];
  currentQuestion: Question = new Question();
  lastCurrentIndex = 0;
  totalOfQuestions = 0;
  totalRepliedQuestions = 0;
  routeTo: '' | 'passport';
  showQuestion: boolean;
  viewOnly = false;
  dataPoints: DataPoint[];
  isRecalculateExcludedQuestions = false;
  previouslyAddedAnswers: Question[] = [];
  engagementQuestionnaire: Questionnaire;

  private _currentIndex = 0;

  constructor(
    public router: Router,
    public alertSvc: AlertService,
    public connectSvc: ConnectService,
    public loadingController: LoadingController,
    private translate: TranslateService,
    private logicParser: LogicParserService,
    private platform: Platform,
    private photoVideoService: PhotoVideoService,
    private objectURLsvc: ObjectUrlService,
    private sentryService: SentryService,
    private eventBusSvc: EventBusService,
    private storage: StorageService
  ) {}

  public reset() {
    this.title = '';
    this.questionnaire = [];
    this.currentQuestion = new Question();
    this.currentIndex = 0;
    this.lastCurrentIndex = 0;
    this.totalOfQuestions = 0;
    this.totalRepliedQuestions = 0;
  }

  get currentQuestionGroup(): QuestionGroup {
    let result: QuestionGroup = {
      id: null,
      questions: [],
    };
    if (!this.currentQuestion) {
      return result;
    }
    let found = false;
    this.questionGroups.some(questionGroup => {
      questionGroup.questions.some(question => {
        if (question.id === this.currentQuestion.id) {
          result = questionGroup;
          found = true;
          return found;
        }
      });
      return found;
    });
    return result;
  }

  get totalOfIncludedQuestions(): number {
    let total = 0;
    this.questionGroups.forEach(questionGroup => {
      const numberOfIncludedQuestions = questionGroup.questions.filter(q => !q.excluded).length;
      total += numberOfIncludedQuestions;
    });
    return total;
  }

  public nextQuestion() {
    this.photoVideoService.toggleVerifyQuestion(this.currentQuestion.id, true);
    /* istanbul ignore next reason: test due in next card WM-3143 */
    switch (true) {
      case this.logicParser.isSupported(this.questionnaire):
        const index = this.getNextQuestionIndex(this.currentIndex);
        if (index !== -1) {
          this.clearAnswersBetween(this.currentIndex + 1, index);
          this.setAndValidateQuestion(index);
        }
        break;
      case this.currentQuestion.routingLogic.length > 0:
        let goToId: number = null;
        this.currentQuestion.routingLogic.forEach(logic => {
          if (logic.option_ids.indexOf(this.currentQuestion.userSelections[0]) !== -1) {
            goToId = logic.question_id;
            return;
          }
        });
        if (goToId) {
          const goToIndex = this.questionnaire.findIndex(question => question.id === goToId);
          this.clearAnswersBetween(this.currentIndex + 1, goToIndex);
          this.setAndValidateQuestion(goToIndex);
        } else this.setSubsequentQuestion();
        break;
      default:
        this.setSubsequentQuestion();
    }
  }

  findQuestionIndex(goToId) {
    const goToIndex = this.questionnaire.findIndex(question => question.id === goToId);
    for (let i = this.currentIndex + 1; i < goToIndex; i += 1) {
      this.questionnaire[i].userAnswer = null;
      this.questionnaire[i].userSelections = [];
    }
    return goToIndex;
  }

  clearAnswersBetween(fromIndex: number, toIndex: number) {
    for (let i = fromIndex; i < toIndex; i++) {
      this.questionnaire[i].userAnswer = null;
      this.questionnaire[i].userSelections = [];
    }
  }

  public setSubsequentQuestion() {
    this.setAndValidateQuestion(this.currentIndex + 1);
  }

  public previousQuestion() {
    if (this.currentIndex <= 0) return;
    /* istanbul ignore next reason: test due in next card WM-3143 */
    if (this.logicParser.isSupported(this.questionnaire)) {
      const index = this.getPreviousQuestionIndex(this.currentIndex);
      if (index !== -1) {
        this.setAndValidateQuestion(index);
      }
    } else this.setQuestionaireRouting();

    this.setIndexToFirstQuestionInGroup();
  }

  public setIndexToFirstQuestionInGroup() {
    const startIdx = this.getFirstIncludedQuestionIdxInTheCurrentGroup();
    if (this.currentIndex > startIdx) {
      this.setAndValidateQuestion(startIdx);
    }
  }

  setQuestionaireRouting() {
    let goToIndex: number = null;
    this.questionnaire.forEach((question, index) => {
      if (index >= this.currentIndex) return;
      if (!Array.isArray(question.routingLogic)) return;
      question.routingLogic.forEach(logic => {
        if (logic.question_id === this.currentQuestion.id && logic.option_ids.indexOf(question.userSelections[0]) !== -1) {
          goToIndex = index;
          return;
        }
      });
      if (goToIndex) return;
    });
    this.setAndValidateQuestion(goToIndex != null ? goToIndex : this.currentIndex - 1);
  }

  /* istanbul ignore next reason: test due in next card WM-3143 */
  getQuestionIndexConditionally(questionIndex: number, nextIndex: 1 | -1) {
    if (this.checkQuestionIndex(questionIndex, nextIndex)) return questionIndex;
    const question = this.questionnaire[questionIndex];

    const skip = this.shouldSkipQuestion(question);

    if (skip || (nextIndex === -1 && question == null)) return this.getQuestionIndexConditionally(questionIndex + nextIndex, nextIndex);

    return questionIndex;
  }

  shouldSkipQuestion(question: Question): boolean {
    let isLogicValid = true;

    let skip = false;
    let error = null;
    try {
      skip =
        question != null &&
        (isLogicValid = this.logicParser.isValid(question.conditionalLogic)) &&
        !this.logicParser.execute(question.conditionalLogic, this.questionnaire, this.dataPoints);
    } catch (e) {
      isLogicValid = false;
      error = e;
    }

    if (!isLogicValid && question.conditionalLogic) {
      this.sentryService.setMessageForSentryWithErrorInfo(`Invalid conditional logic`, SentrySeverityLevel.Debug, {
        question,
        error,
      });
    }

    return skip;
  }

  checkQuestionIndex(questionIndex, nextIndex): boolean {
    return !this.questionnaire.length || questionIndex === -1 || (questionIndex === this.questionnaire.length && nextIndex === 1);
  }

  public setQuestionnaire(
    questionnaire: Questionnaire,
    viewOnly: boolean,
    answers: QuestionAnswer[],
    refresh?: boolean,
    resultId?: number
  ) {
    this.engagementQuestionnaire = questionnaire;
    this.title = questionnaire.title;
    this.routeTo = questionnaire.route_to_after_completion;
    this.showQuestion = questionnaire.show_question;
    const mappedQuestionnaire = this.mapQuestions(questionnaire);
    const staledQuestionnaire = { ...this.questionnaire };
    this.questionGroups = this.groupQuestions(mappedQuestionnaire);
    const sortedQuestionnaire = [];
    this.questionGroups.forEach(questionGroup => sortedQuestionnaire.push(...questionGroup.questions));
    this.questionnaire = sortedQuestionnaire;
    this.dataPoints = questionnaire.data_points;
    this.mapQuestionAnswers(viewOnly, answers, resultId);

    // update nested data-point for injected data-points
    this.dataPoints.forEach(dataPoint => {
      dataPoint.value = this.pipeDataPoints(dataPoint.value, this.dataPoints);
    });

    // update local data-points
    const engagementQuestionMap: Record<number, EngagementQuestion> = {};
    questionnaire.questions.forEach(question => (engagementQuestionMap[question.id] = question));
    const localDataPoints = this.createLocalDataPoints(this.questionnaire);
    const mergedDataPoints = [...this.dataPoints, ...localDataPoints];
    this.questionnaire.forEach(question => this.injectDataPoints(question, engagementQuestionMap[question.id], mergedDataPoints));

    if (refresh) {
      this.questionnaire.forEach((question, index) => {
        question.userAnswer = staledQuestionnaire[index].userAnswer;
        question.userSelections = staledQuestionnaire[index].userSelections;
      });
    }

    this.totalOfQuestions = this.questionnaire.length;
    let currentIndex = this.currentIndex;
    /* istanbul ignore next reason: test due in next card WM-3143 */
    if (this.logicParser.isSupported(this.questionnaire)) {
      currentIndex = this.getQuestionIndexConditionally(currentIndex, 1);
    }
    const previousConditionalIndex = this.getQuestionIndexConditionally(currentIndex, -1);
    /* istanbul ignore next reason: test due in next card WM-3143 */
    if (currentIndex === this.totalOfQuestions) {
      currentIndex = previousConditionalIndex;
    }

    // if the last photo/video question is not verified yet then set the current question back to that question
    const previousIndex = previousConditionalIndex < currentIndex ? previousConditionalIndex : currentIndex - 1;
    if (
      this.photoVideoService.unverifiedQuestionIds.length &&
      this.questionnaire[previousIndex] &&
      this.photoVideoService.unverifiedQuestionIds.includes(this.questionnaire[previousIndex].id)
    ) {
      currentIndex = previousIndex;
    }

    this.setQuestion(currentIndex);
    this.recalculateAllQuestionsDependencies();
    this.recalculateExcludedQuestions(0);
    this.setIndexToFirstQuestionInGroup();

    // goto next question when continuing
    if (answers.length && this.isQuestionAnswered(this.currentQuestion)) {
      this.routingLogic(this.currentQuestion);
    }
  }

  public mapQuestions(questionnaire: Questionnaire): Question[] {
    const questions: Question[] = [];
    questionnaire.questions.forEach((engagementQuestion, index) => {
      if (
        [
          QuestionTypes.MultipleChoiceQuestion,
          QuestionTypes.SingleChoiceQuestion,
          QuestionTypes.RangeSliderQuestion,
          QuestionTypes.TextQuestion,
          QuestionTypes.NumberQuestion,
          QuestionTypes.EmailQuestion,
          QuestionTypes.Instruction,
          QuestionTypes.DateTimeQuestion,
          QuestionTypes.TimeQuestion,
          QuestionTypes.YearQuestion,
          QuestionTypes.DateQuestion,
          QuestionTypes.HappyTechQuestion,
          QuestionTypes.PhotoQuestion,
          QuestionTypes.VideoQuestion,
        ].indexOf(engagementQuestion.type) === -1
      )
        return;

      const question = this.prepareQuestion(engagementQuestion, questionnaire.data_points);

      if (!EXCLUDED_GROUPED_QUESTIONS_TYPE.includes(question.type)) {
        question.questionGroupId = engagementQuestion.question_group_id;
      }

      if (question.conditionalLogic && this.logicParser.isValid(question.conditionalLogic)) {
        question.excluded = true;
      }

      questions.push(question);
    });

    return questions;
  }

  public mapQuestionAnswers(viewOnly: boolean, answers: QuestionAnswer[], resultId?) {
    let dontSetIndex = viewOnly;
    let currentIdx = 0;
    let count = 0;
    if (viewOnly || resultId) {
      while (currentIdx < this.questionnaire.length) {
        const question = this.questionnaire[currentIdx];
        if (question.type === QuestionTypes.HappyTechQuestion) {
          const answerIndex = answers.findIndex(x => x.question_id === question.id);
          if (answerIndex >= 0 && answers.length >= answerIndex + 7) {
            const vitals: HappitechResults = {
              HR_BPM: parseInt(answers[answerIndex + 1].value, 10),
              HR_CONFIDENCE: parseInt(answers[answerIndex + 3].value, 10),
              HRV_FREQUENCY: null,
              HRV_RMSDD: parseInt(answers[answerIndex + 6].value, 10),
              HRV_PNN50: parseInt(answers[answerIndex + 7].value, 10),
              HRV_AVNN: parseInt(answers[answerIndex + 4].value, 10),
              HRV_SDNN: parseInt(answers[answerIndex + 5].value, 10),
              RAWDATA: null,
              AFB_PROBABILITY: parseInt(answers[answerIndex + 2].value, 10),
            };
            question.vitalsData = vitals;
          }
        }
        const indexHasbeenSet = this.setAnswersAndCurrentIndex(answers, currentIdx, question, dontSetIndex);
        if (!indexHasbeenSet) {
          dontSetIndex = true;
        }

        currentIdx++;
        count++;
      }
    }
  }

  public groupQuestions(questions: Question[]): QuestionGroup[] {
    const results: QuestionGroup[] = [];
    const addToGroup = (question: Question) => {
      const group = results.find(gq => question.questionGroupId && gq.id === question.questionGroupId);
      if (group) {
        group.questions.push(question);
      } else {
        const newgroup = new QuestionGroup();
        newgroup.id = question.questionGroupId;
        newgroup.questions.push(question);
        results.push(newgroup);
      }
    };

    questions.forEach(q => {
      addToGroup(q);
    });
    return results;
  }

  // return true if the new current index has been set
  setAnswersAndCurrentIndex(answers: QuestionAnswer[], index: number, question: Question, viewOnly: boolean): boolean {
    let answer = null;
    let nextIndex = -1;
    answers.some(currentAnswer => {
      if (currentAnswer.question_id === question.id) {
        answer = currentAnswer;
        if (viewOnly) return true;
        nextIndex = question.routingLogic.length > 0 || this.currentIndex >= this.questionnaire.length - 1 ? index : index + 1;
      }
    });

    if (answer !== null && !answer.skipped) this.populateAnswer(question, answer);

    if (question.mandatory && !this.isQuestionAnswered(question)) {
      return false;
    }

    if (nextIndex >= 0) {
      this.currentIndex = nextIndex;
    }
    return true;
  }

  populateAnswer(question: Question, answered) {
    const parseDateTime = (datetime: string, formats: string[]): string => {
      for (const format of formats) {
        const dayjsObj = dayjs(datetime, format);
        if (dayjsObj.isValid()) {
          return dayjsObj.format('YYYY-MM-DDTHH:mm:ssZ');
        }
      }
      return null;
    };
    try {
      switch (true) {
        case [QuestionTypes.SingleChoiceQuestion, QuestionTypes.MultipleChoiceQuestion].includes(question.type):
          question.userSelections = Array.isArray(answered.option_ids) ? answered.option_ids.map(i => Number(i)) : [];
          break;
        case question.type === QuestionTypes.DateQuestion:
          question.userAnswer = answered.value ? parseDateTime(answered.value, ['DD/MM/YYYY']) : null;
          break;
        case question.type === QuestionTypes.DateTimeQuestion:
          question.userAnswer = answered.value ? parseDateTime(answered.value, ['DD/MM/YYYY, HH:mm:ss', 'DD/MM/YYYY, HH:mm']) : null;
          break;
        case question.type === QuestionTypes.TimeQuestion:
          question.userAnswer = answered.value ? parseDateTime(answered.value, ['HH:mm:ss', 'HH:mm']) : null;
          break;
        case question.type === QuestionTypes.Instruction:
          question.userAnswer = 'Instruction';
          break;
        case question.type === QuestionTypes.PhotoQuestion: {
          const file: File = { name: 'image.jpg', type: 'image/jpg', fullPath: answered.photo_url, logo: '' };
          question.videoPhotoResult = file.fullPath ? file : null;
          break;
        }
        case question.type === QuestionTypes.VideoQuestion: {
          const file: File = { name: 'video.webm', type: 'video/webm', fullPath: answered.video_url, logo: DEFAULT_VIDEO_THUMBNAIL };
          question.videoPhotoResult = file.fullPath ? file : null;
          break;
        }
        case [
          QuestionTypes.TextQuestion,
          QuestionTypes.EmailQuestion,
          QuestionTypes.NumberQuestion,
          QuestionTypes.RangeSliderQuestion,
          QuestionTypes.YearQuestion,
        ].includes(question.type):
          question.userAnswer = answered === null ? '' : answered.value;
          break;
      }
      question.uploaded = true;
    } catch (error) {
      console.error('Failed to parse the user answer', { answered, type: question.type });
    }
    return question;
  }

  prepareQuestion(engagementQuestion: EngagementQuestion, dataPoints: DataPoint[]): Question {
    const question = new Question();
    question.id = engagementQuestion.id;
    question.save = engagementQuestion.save;
    question.titleHidden = engagementQuestion.title_hidden;
    question.type = engagementQuestion.type;
    question.minimum = engagementQuestion.minimum;
    question.maximum = engagementQuestion.maximum;
    question.defaultValue = engagementQuestion.default_value;
    question.orientation = engagementQuestion.orientation;
    question.minimumLength = engagementQuestion.minimum_length === null ? 0 : engagementQuestion.minimum_length;
    question.maximumLength = engagementQuestion.maximum_length === null ? 9999 : engagementQuestion.maximum_length;
    question.mandatory = engagementQuestion.mandatory;
    question.step = engagementQuestion.step;
    question.routingLogic = engagementQuestion.routing_logic;
    question.conditionalLogic = engagementQuestion.conditional_logic;
    question.noVitals = engagementQuestion.no_value;
    question.shortName = engagementQuestion.short_name;
    question.allowVerify = engagementQuestion.allow_verify;
    question.overlay = engagementQuestion.overlay;
    question.camera = engagementQuestion.camera;
    question.restrictVideoLength = engagementQuestion.restrict_video_length;
    question.maxVideoTime = engagementQuestion.max_video_time;
    question.hasInstructions = engagementQuestion.allow_instructions;
    question.allowRecordingInstructions = engagementQuestion.allow_recording_instructions;
    question.decimalPlaces = engagementQuestion.decimal_places;
    question.isVideo = engagementQuestion.type === QuestionTypes.VideoQuestion;
    question.uploaded = false;
    /*istanbul ignore next*/
    question.validationRules = this.mapValidationRules(engagementQuestion.field_validations ?? []);
    question.userAnswer = null;
    question.confirmQuestionSkip = engagementQuestion.confirm_skip;
    this.injectDataPoints(question, engagementQuestion, dataPoints);

    return question;
  }

  injectDataPoints(question: Question, engagementQuestion: EngagementQuestion, dataPoints: DataPoint[]) {
    question.title = this.pipeDataPoints(engagementQuestion.title, dataPoints);
    question.subtitle = this.pipeDataPoints(engagementQuestion.subtitle, dataPoints);
    question.infoText = this.pipeDataPoints(engagementQuestion.info_text, dataPoints);
    question.minimumLabel = this.pipeDataPoints(engagementQuestion.minimum_label, dataPoints);
    question.maximumLabel = this.pipeDataPoints(engagementQuestion.maximum_label, dataPoints);
    question.placeholder = this.pipeDataPoints(engagementQuestion.placeholder, dataPoints);
    question.dataLabel = this.pipeDataPoints(engagementQuestion.data_label, dataPoints);
    question.description = this.pipeDataPoints(engagementQuestion.description, dataPoints);
    question.choices = this.mapOptions(engagementQuestion.options, dataPoints);
    question.footer = this.pipeDataPoints(engagementQuestion.footer, dataPoints);
    question.allowVerifyText = this.pipeDataPoints(engagementQuestion.allow_verify_text, dataPoints);
    if (Array.isArray(engagementQuestion.question_instructions) && engagementQuestion.question_instructions.length) {
      question.questionInstructions = engagementQuestion.question_instructions.map(instruction => {
        return { ...instruction, instructions: this.pipeDataPoints(instruction.instructions, dataPoints) };
      });
    }
    question.recordingInstructions = this.pipeDataPoints(engagementQuestion.recording_instructions, dataPoints);
  }

  public pipeDataPoints(value: string, dataPoints: DataPoint[]): string {
    if (!Array.isArray(dataPoints) || !dataPoints.length || typeof value !== 'string') {
      return value;
    }
    const format = {
      dateTime: /^\d{1,2}\/\d{1,2}\/\d{4},\s\d{1,2}:\d{1,2}:\d{1,2}$/g,
      time: /^\d{1,2}:\d{1,2}:\d{1,2}$/g,
    };

    const getReplacement = (word: string, dataPoints: DataPoint[]): string => {
      const purifiedWord = DOMPurify.sanitize(word, { ALLOWED_TAGS: [] });
      let replacement = purifiedWord;
      dataPoints.forEach(dataPoint => {
        const regexp = new RegExp(`\\\[${dataPoint.identifier}\\\]`, 'g');
        if (dataPoint.value && format.dateTime.test(dataPoint.value.trim())) {
          const datetime = dayjs(dataPoint.value.trim(), 'D/M/YYYY, H:m:s');
          replacement = replacement.replace(regexp, datetime.format('DD/MM/YYYY HH:mm'));
        } else if (dataPoint.value && format.time.test(dataPoint.value.trim())) {
          const datetime = dayjs(dataPoint.value.trim(), 'H:m:s');
          replacement = replacement.replace(regexp, datetime.format('HH:mm'));
        } else {
          replacement = replacement.replace(regexp, dataPoint.value);
        }
      });
      const removeBracket = (s: string): string => {
        return s.replace('[', '').replace(']', '');
      };
      const extraHtmlTags = removeBracket(word).split(removeBracket(purifiedWord));
      if (extraHtmlTags.length === 2) {
        return extraHtmlTags[0] + replacement + extraHtmlTags[1];
      }
      return replacement;
    };

    const wordsInsideBrackets = value.match(/\[([^\]]+)\]/g);
    if (wordsInsideBrackets && wordsInsideBrackets.length) {
      wordsInsideBrackets.forEach(word => {
        value = value.replace(word, getReplacement(word, dataPoints));
      });
    }
    return value;
  }

  public mapOptions(engagementOptions: EngagementOption[], dataPoints: DataPoint[]): Choice[] {
    const answers: Choice[] = [];
    engagementOptions.forEach(engagementOption => {
      const answer = new Choice(engagementOption.id, this.pipeDataPoints(engagementOption.text, dataPoints));
      answers.push(answer);
    });
    return answers;
  }

  /*istanbul ignore next*/
  public mapValidationRules(validations: EngagementValidation[]) {
    let validationRules: ValidationRule[] = [];
    validationRules = validations.map(validation => {
      const rule: ValidationRule = {
        id: validation.id,
        type: this.mapMessageStatus(validation.validation_type),
        value: validation.value,
        operator: validation.operator,
        text: validation.message,
      };
      return rule;
    });

    return validationRules;
  }
  /*istanbul ignore next*/
  public mapMessageStatus(type: ValidationType): MessageStatus {
    switch (type) {
      case 'warning':
        return 'warning';
      case 'exclusion':
        return 'exclusion';
      case 'message':
        return 'message';
    }
  }

  public setQuestion(index: number) {
    this.currentIndex = index;
    if (index > this.questionnaire.length - 1) {
      this.currentQuestion = null;
    } else {
      this.currentQuestion = this.questionnaire[index];
      this.currentQuestion.validationMessages = [];
    }

    let answersCount = 0;
    this.questionnaire.forEach(question => {
      if (question.userAnswer || question.userSelections.length > 0) {
        answersCount += 1;
      }
    });
    this.totalRepliedQuestions = answersCount;
    this.eventBusSvc.publish(EventTopic.CurrentIncludedQuestionsUpdated, this.currentIncludedQuestions);
  }

  setAndValidateQuestion(index: number) {
    this.setQuestion(index);

    /*istanbul ignore next*/
    if (this.currentQuestion && (this.currentQuestion.userAnswer || this.currentQuestion.userSelections.length)) {
      this.validateAnswer(this.currentQuestion, false);
      this.processDataPoints(this.currentQuestion);
    }
  }

  routingLogic(currentQuestion: Question) {
    if (!currentQuestion || !currentQuestion.routingLogic || currentQuestion.routingLogic.length <= 0) {
      return;
    }

    let goToId: number = null;
    currentQuestion.routingLogic.forEach(logic => {
      // istanbul ignore else
      if (logic.option_ids.indexOf(currentQuestion.userSelections[0]) !== -1) {
        goToId = logic.question_id;
        return;
      }
    });
    if (goToId) {
      const goToIndex = this.findQuestionIndex(goToId);
      this.currentQuestion = this.questionnaire[goToIndex];
      this.currentIndex = goToIndex;
    } else this.setSubsequentQuestion();
  }

  public async validateAnswer(question: Question, recalculateExcludedQuestion = true) {
    question.validationMessages = [];

    if (!question.validationRules) {
      return;
    }
    question.validationRules.forEach(rule => {
      const answer: string | number | null = null;
      const value: string | number | null = null;
      this.validationAnswerWithDifferentParams(question, answer, rule, value);
    });

    if (!this.viewOnly && recalculateExcludedQuestion) {
      this.recalculateDependentQuestions(question);
    }
    this.validateCurrentQuestionGroup(false);
  }

  processDataPoints(currentQuestion: Question) {
    const currentIndex = this.questionnaire.findIndex(question => question.id === currentQuestion.id);

    const processedGroups = [];
    let nextIndex = this.getNextQuestionIndex(currentIndex);
    while (nextIndex < this.questionnaire.length) {
      const nextQuestion = this.questionnaire[nextIndex];

      let indexOfGroupQuestions = -1;
      const group = this.questionGroups.find(group =>
        group.questions.some((q, index) => {
          indexOfGroupQuestions = index;
          return q.id === nextQuestion.id;
        })
      );

      // skip already processed groups
      if (processedGroups.includes(group)) {
        nextIndex = this.getNextQuestionIndex(nextIndex);
        continue;
      }
      processedGroups.push(group);

      if (group.questions.length !== 1) {
        let skip = true;
        group.questions.forEach((q, index) => {
          // skip until the next question found
          if (q.id === nextQuestion.id) skip = false;
          if (skip) return;
          this.updateDataPoints(q, group, index);
        });
      } else {
        this.updateDataPoints(nextQuestion, group, indexOfGroupQuestions);
      }

      nextIndex = this.getNextQuestionIndex(nextIndex);
    }
  }

  updateDataPoints(question: Question, group: QuestionGroup, indexOfGroupQuestions: number) {
    const indexOfQuestionnaire = this.questionnaire.findIndex(q => q.id === question.id);
    const engagementQuestion = this.engagementQuestionnaire.questions.find(eq => eq.id === question.id);

    const localDataPoints = this.createLocalDataPoints(this.questionnaire);

    // create new refs for change detection
    const newQuestion = new Question();
    Object.assign(newQuestion, question);

    this.injectDataPoints(newQuestion, engagementQuestion, [...this.dataPoints, ...localDataPoints]);

    this.questionnaire[indexOfQuestionnaire] = newQuestion;
    this.questionnaire = [...this.questionnaire];

    group.questions[indexOfGroupQuestions] = newQuestion;
    group.questions = [...group.questions];
  }

  validationAnswerWithDifferentParams(question: Question, answer, rule, value) {
    switch (true) {
      case [QuestionTypes.NumberQuestion, QuestionTypes.RangeSliderQuestion].includes(question.type):
        this.validateNumberRangeSlider(question, rule);
        break;
      case [QuestionTypes.DateQuestion, QuestionTypes.DateTimeQuestion, QuestionTypes.TimeQuestion, QuestionTypes.YearQuestion].includes(
        question.type
      ):
        this.validateDate(question, rule);
        break;
      case [QuestionTypes.MultipleChoiceQuestion, QuestionTypes.SingleChoiceQuestion].includes(question.type):
        this.findCheckAndAddMessage(question, rule, question.userSelections.map(String));
        break;
      default:
        // istanbul ignore else
        if (question.userAnswer) {
          answer = question.userAnswer;
          value = rule.value;
          this.checkAndAddMessage(question, rule, answer, value);
        }
    }
  }

  public needRecalculation(q: Question) {
    return q.excluded && this.isQuestionAnswered(q) && q.type !== QuestionTypes.Instruction;
  }

  public recalculateExcludedQuestions(currentIndex: number) {
    const now = performance.now();
    try {
      this.logicParser.storeContext();
      this.isRecalculateExcludedQuestions = true;
      const hideQuestionsIds: number[] = [];

      let nextIdx = 0;
      this.questionnaire.forEach((q, idx) => {
        if (idx < nextIdx || idx < currentIndex) {
          return;
        }
        nextIdx = this.getQuestionIndexConditionally(idx, 1);
        if (nextIdx > idx) {
          for (let i = idx; i < nextIdx; i++) {
            hideQuestionsIds.push(this.questionnaire[i].id);
          }
        }
      });

      let needsRecalculation = false;
      this.questionnaire.forEach((q, idx) => {
        if (idx < currentIndex) {
          return;
        }
        q.excluded = hideQuestionsIds.includes(q.id);
        if (this.needRecalculation(q)) {
          this.removeQuestionAnswer(q);
          needsRecalculation = true;
        }
      });

      if (needsRecalculation) {
        return this.recalculateExcludedQuestions(currentIndex);
      }
    } catch (error) {
      this.sentryService.setMessageForSentryWithErrorInfo(`Failed to recalculate excluded questions`, SentrySeverityLevel.Error, {
        error,
      });
    }
    this.isRecalculateExcludedQuestions = false;
    this.logicParser.clearContext();
    // eslint-disable-next-line no-console
    console.debug(`recalculateExcludedQuestions took ${performance.now() - now} ms`);
  }

  public recalculateDependentQuestions(question: Question) {
    const now = performance.now();
    try {
      this.isRecalculateExcludedQuestions = true;
      question.dependentQuestionIds.forEach(id => {
        const dependentQuestion = this.questionnaire.find(q => q.id === id);
        if (dependentQuestion) {
          const skip = this.shouldSkipQuestion(dependentQuestion);
          dependentQuestion.excluded = skip || dependentQuestion == null;
          if (this.needRecalculation(dependentQuestion)) {
            this.removeQuestionAnswer(dependentQuestion);
            this.recalculateDependentQuestions(dependentQuestion);
          }
        }
      });
    } catch (error) {
      this.sentryService.setMessageForSentryWithErrorInfo(`Failed to recalculate dependent questions`, SentrySeverityLevel.Error, {
        error,
      });
    }
    this.isRecalculateExcludedQuestions = false;
    // eslint-disable-next-line no-console
    console.debug(`recalculateDependentQuestions took ${performance.now() - now} ms`);
  }

  createLocalDataPoints(questions: Question[]) {
    const localDataPoints: DataPoint[] = [];

    for (let idx = 0; idx < questions.length; idx++) {
      const question = questions[idx];
      const dataPointOptions: DataPointOption[] = [];
      let dataPointValue = '';

      const choiceMap = question.choices.reduce((map, choice) => {
        // Update nested data-points from same form
        const value = this.pipeDataPoints(choice.text, [...this.dataPoints, ...localDataPoints]);
        dataPointOptions.push({
          id: choice.id,
          value,
        });
        map[choice.id] = value;
        return map;
      }, {});

      switch (question.type) {
        case QuestionTypes.SingleChoiceQuestion:
        case QuestionTypes.MultipleChoiceQuestion:
          dataPointValue = question.userSelections.map(selection => choiceMap[selection]).join(', ');
          break;
        case QuestionTypes.DateQuestion:
          if (question.userAnswer != null) dataPointValue = getDate(question.userAnswer as string);
          break;
        case QuestionTypes.DateTimeQuestion:
          if (question.userAnswer != null) dataPointValue = getDateTime(question.userAnswer as string);
          break;
        case QuestionTypes.TimeQuestion:
          if (question.userAnswer != null) dataPointValue = getTime(question.userAnswer as string);
          break;
        case QuestionTypes.YearQuestion:
          if (question.userAnswer != null) dataPointValue = new Date(question.userAnswer).getFullYear().toString();
          break;
        case QuestionTypes.PhotoQuestion:
          if (question.videoPhotoResult) dataPointValue = 'PhotoAttachment';
          break;
        case QuestionTypes.VideoQuestion:
          if (question.videoPhotoResult) dataPointValue = 'VideoAttachment';
          break;
        default:
          if (question.userAnswer != null) dataPointValue = String(question.userAnswer);
      }

      localDataPoints.push({
        identifier: question.shortName,
        question_type: question.type,
        value: dataPointValue,
        options_map: dataPointOptions,
      });
    }

    return localDataPoints;

    /**
     * @returns format to D/M/Y
     */
    function getDate(value: string) {
      const date = new Date(value);
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    }
    /**
     * @returns format to H:m:s
     */
    function getTime(value: string) {
      const date = new Date(value);
      return `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
    }
    /**
     * @returns format to D/M/Y, H:m:s
     */
    function getDateTime(value: string) {
      return `${getDate(value)}, ${getTime(value)}`;
    }
  }

  public validateNumberRangeSlider(question: Question, rule: ValidationRule) {
    let answer: string | number | null = null;
    let value: string | number | null = null;
    if (question.userAnswer) {
      answer = parseFloat(question.userAnswer.toString());
      value = parseFloat(rule.value.toString());
      this.checkAndAddMessage(question, rule, answer, value);
    }
  }

  public validateDate(question: Question, rule: ValidationRule) {
    // answer & value would be unix timestamp (except year)
    let answer: number = null;
    let value: number = null;
    if (question.userAnswer) {
      let dayjsObj = rule.value === 'NOW' ? dayjs() : null;
      try {
        switch (question.type) {
          case QuestionTypes.DateTimeQuestion:
            dayjsObj = dayjsObj || dayjs(`${rule.value}`, 'YYYY-MM-DDTHH:mm');
            value = dayjsObj.set('second', 0).set('millisecond', 0).unix();
            answer = dayjs(`${question.userAnswer}`).set('second', 0).set('millisecond', 0).unix();
            break;
          case QuestionTypes.DateQuestion:
            dayjsObj = dayjsObj || dayjs(`${rule.value}`, 'DD/MM/YYYY');
            value = dayjsObj.set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0).unix();
            answer = dayjs(`${question.userAnswer}`).set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0).unix();
            break;
          case QuestionTypes.TimeQuestion:
            dayjsObj = dayjsObj || dayjs(`2021-01-01 ${rule.value}`, 'YYYY-MM-DD HH:mm');
            value = dayjsObj.set('second', 0).set('millisecond', 0).unix();
            answer = dayjs(`${question.userAnswer}`).set('year', 2021).set('month', 0).set('date', 1).set('second', 0).unix();
            break;
          case QuestionTypes.YearQuestion:
            value = dayjsObj ? Number(dayjsObj.format('YYYY')) : Number(rule.value);
            answer = Number(dayjs(`${question.userAnswer}`).format('YYYY'));
            break;
        }
        this.checkAndAddMessage(question, rule, answer, value);
      } catch (error) {
        // istanbul ignore next
        console.error(error);
        // istanbul ignore next
        this.sentryService.setMessageForSentryWithErrorInfo(
          `ValidateDate failing for question ${question.id}`,
          SentrySeverityLevel.Critical,
          { userAnswer: question.userAnswer, value: rule.value, questionType: question.type }
        );
      }
    }
  }

  public checkAndAddMessage(question: Question, rule: ValidationRule, answer: string | number, value: string | number) {
    let matched = false;
    switch (true) {
      case rule.operator === '==' && answer === value:
        matched = true;
        break;
      case rule.operator === '!=' && answer !== value:
        matched = true;
        break;
      case rule.operator === '<' && answer < value:
        matched = true;
        break;
      case rule.operator === '>' && answer > value:
        matched = true;
        break;
      case rule.operator === '<=' && answer <= value:
        matched = true;
        break;
      case rule.operator === '>=' && answer >= value:
        matched = true;
        break;
    }
    if (matched) question.validationMessages.push({ message: rule.text, status: rule.type });
  }

  public findCheckAndAddMessage(question: Question, rule: ValidationRule, userSelections: string[]) {
    if (rule.operator === '==' && userSelections.find(selection => selection === rule.value)) {
      question.validationMessages.push({ message: rule.text, status: rule.type });
    }

    if (rule.operator === '!=' && !userSelections.find(selection => selection === rule.value)) {
      question.validationMessages.push({ message: rule.text, status: rule.type });
    }
  }

  public async addQuestionErrorMessages(question: Question, messageKeys: string[]) {
    const translations = await this.translate.get(messageKeys).toPromise();
    messageKeys.forEach(k => {
      // istanbul ignore else
      if (question.currentErrorMessages.indexOf(translations[k]) < 0) {
        question.currentErrorMessages.push(translations[k]);
      }
    });
  }

  public async exit(): Promise<boolean> {
    const translations = await this.translate.get(['QuestionnaireService.header', 'QuestionnaireService.message']).toPromise();

    const response: AlertOption = await this.alertSvc.presentConfirmation(
      translations['QuestionnaireService.header'],
      '',
      translations['QuestionnaireService.message'],
      ''
    );
    return response.option;
  }

  processErrorMessageOnSubmit(error) {
    if (error.status === 0) {
      return this.translate.instant('ErrorMessages.questionSubmitFail');
    }

    if (
      error.error &&
      Array.isArray(error.error.scheduled_engagement_id) &&
      error.error.scheduled_engagement_id.length &&
      error.error.scheduled_engagement_id[0] === 'Person has already completed this questionnaire'
    ) {
      return this.translate.instant('ErrorMessages.pleaseExitTheSurvey');
    }

    let message = '';
    Object.keys(error.error).forEach(key => {
      message += error.error[key];
    });
    return message;
  }

  public generatePath(apiBase, introductionTask, resultId, recordId, engagement, engagementSvc) {
    let path = '';
    const record = this.getRecord(apiBase, resultId, recordId);
    if (introductionTask) path = `/program_reports/${engagement}/results` + record;
    else path = `/scheduled_engagements/${engagementSvc.engagement.id}/results` + record;
    return path;
  }

  getRecord(apiBase, resultId, recordId): string {
    return apiBase === 'participant' ? (resultId ? `/${resultId}/${recordId}` : `/${recordId}`) : resultId ? `/${resultId}` : '';
  }

  public async goToHomeScreen(isLastQuestion: boolean, questionnaireSvc, isPassportFeatureEnabled, fromSettingsPage) {
    if (!isLastQuestion) {
      return;
    }

    if (questionnaireSvc.routeTo === 'passport' && isPassportFeatureEnabled) {
      return this.router.navigate(['tabs', 'passport']);
    } else {
      if (!fromSettingsPage) this.storage.set('questionnaireSubmitted', 'true');
      return this.router.navigate([fromSettingsPage ? '/tabs/settings' : '/tabs']);
    }
  }

  public isQuestionAnswered(question: Question): boolean {
    if (!question) {
      return true;
    }
    if (question.type === QuestionTypes.Instruction) {
      return true;
    }
    if (isPhotoVideoQuestionType(question.type) && question.videoPhotoResult) {
      return true;
    }
    if (question.userAnswer != null && question.userAnswer !== '') {
      return true;
    }

    return Boolean(question.vitalsData) || question.userSelections.length > 0;
  }

  public removeQuestionAnswer(question: Question, emitEvent = true) {
    question.videoPhotoResult = null;
    question.manualVideoPhotoUpload = false;
    question.userAnswer = null;
    question.vitalsData = null;
    question.userSelections = [];
    question.uploaded = false;
    question.isAnswerValid = true;
    if (emitEvent) {
      this.eventBusSvc.publish(EventTopic.QuestionAnswerRemoved, question);
    }
  }

  public isCurrentQuestionGroupAnswered(): boolean {
    let result = true;
    this.currentIncludedQuestions.forEach(question => {
      if (!this.isQuestionAnswered(question)) {
        result = false;
      }
    });
    return result;
  }

  public isPhotoVideoQuestionNotAnswered(question: Question) {
    switch (true) {
      case isPhotoVideoQuestionType(question.type):
        if (!isPhotoVideoResultUploaded(question.videoPhotoResult) && !question.uploaded) return true;
        else return false;
      default:
        return true;
    }
  }

  public removeAllFiles(questionnaire) {
    questionnaire.forEach(question => {
      if (this.isPhotoOrVideoQuestion(question) && this.hasPhotoOrVideoAnswer(question)) {
        if (this.platform.is('cordova')) {
          this.photoVideoService.removeFile(question.videoPhotoResult.fullPath);
        } else {
          this.objectURLsvc.revokeObjectURL(question.videoPhotoResult.fullPath);
        }
      }
    });
  }

  public isPhotoOrVideoQuestion(question: Question): boolean {
    return question.type === QuestionTypes.PhotoQuestion || question.type === QuestionTypes.VideoQuestion;
  }

  public hasPhotoOrVideoAnswer(question: Question): boolean {
    return !!question?.videoPhotoResult;
  }

  public async validateCurrentQuestionGroup(addCurrentQuestionErrorMessages: boolean = true): Promise<boolean> {
    this.currentIncludedQuestions.forEach(async question => {
      question.currentErrorMessages = [];
      if (!question || !question.mandatory) {
        return true;
      }
      if (!this.isQuestionAnswered(question)) {
        if (addCurrentQuestionErrorMessages) {
          await this.addQuestionErrorMessages(question, ['QuestionnairePage.answerThisQuestion']);
        }
        return false;
      }
    });
    return true;
  }

  public getIndexByQuestionId(id: number): number {
    let idx = -1;
    this.questionnaire.some((q, _idx) => {
      if (q.id === id) {
        idx = _idx;
        return true;
      }
    });
    return idx;
  }

  public get currentIncludedQuestions(): Question[] {
    return this.currentQuestionGroup.questions.filter(q => !q.excluded);
  }

  public set currentIndex(newIdx: number) {
    if (newIdx >= this.lastCurrentIndex) {
      this.lastCurrentIndex = newIdx;
    }
    this._currentIndex = newIdx;
  }

  public get currentIndex(): number {
    return this._currentIndex;
  }

  getCurrentQuestionIndex(ignoreExcluded = false): { start: number; end: number } {
    if (
      this.currentQuestionGroup &&
      this.currentQuestionGroup.questions &&
      Array.isArray(this.currentQuestionGroup.questions) &&
      this.currentQuestionGroup.questions.length
    ) {
      let start = this.getIndexByQuestionId(this.currentQuestionGroup.questions[0].id);
      let end = this.getIndexByQuestionId(this.currentQuestionGroup.questions[this.currentQuestionGroup.questions.length - 1].id);
      if (ignoreExcluded) {
        const numOfExcludedQuestions = this.questionnaire.filter((q, idx) => idx < start && q.excluded).length;
        start -= numOfExcludedQuestions;
        end = start + this.currentIncludedQuestions.length;
      }
      return { start, end };
    }
    return { start: this.currentIndex, end: this.currentIndex };
  }

  getFirstIncludedQuestionIdxInTheCurrentGroup(): number {
    const { start } = this.getCurrentQuestionIndex();
    let inc = 0;
    this.currentQuestionGroup.questions.some(q => {
      if (!q.excluded) {
        return true;
      }
      inc++;
    });
    return start + inc;
  }

  markQuestionAsUploaded(result: Result) {
    if (result && result.result && Array.isArray(result.result.answers_attributes)) {
      result.result.answers_attributes.forEach(resultAnswer => {
        if (resultAnswer.question_id) {
          const questionIdx = this.getIndexByQuestionId(resultAnswer.question_id);
          if (questionIdx > -1 && !this.questionnaire[questionIdx].uploading) {
            this.questionnaire[questionIdx].uploaded = true;
          }
        }
      });
    }
  }

  removeUploadedQuestion(result: Result): Result {
    const newResult = cloneDeep(result);
    if (result && result.result && Array.isArray(result.result.answers_attributes)) {
      newResult.result.answers_attributes = [];
      result.result.answers_attributes.forEach(resultAnswer => {
        if (resultAnswer.question_id) {
          const questionIdx = this.getIndexByQuestionId(resultAnswer.question_id);
          if (questionIdx > -1 && !this.questionnaire[questionIdx].uploaded && !this.questionnaire[questionIdx].uploading) {
            newResult.result.answers_attributes.push(resultAnswer);
          }
        }
      });
      if (!newResult.result.answers_attributes.length) {
        return null;
      }
    }
    return newResult;
  }

  isCurrentGroupNotSynced(): boolean {
    return this.currentIncludedQuestions.some(q => !q.uploaded && !q.uploading && this.isQuestionAnswered(q));
  }

  isFirstQuestionGroup(): boolean {
    if (this.currentIndex === 0) {
      return true;
    }
    try {
      return this.currentQuestionGroup.questions[0].id === this.questionnaire[0].id;
    } catch (error) {
      return false;
    }
  }

  recalculateAllQuestionsDependencies() {
    this.questionnaire.forEach(question => {
      question.dependentQuestionIds = this.logicParser.getDependentQuestionIds(question.shortName, this.questionnaire);
    });
  }
  setPreviouslySavedAnswers() {
    this.previouslyAddedAnswers.forEach(previous => {
      const index = this.questionnaire.findIndex(current => current.id === previous.id);
      if (index >= 0) {
        this.questionnaire[index].userSelections = previous.userSelections;
        this.questionnaire[index].userAnswer = previous.userAnswer;
        this.questionnaire[index].vitalsData = previous.vitalsData;
        this.questionnaire[index].videoPhotoResult = previous.videoPhotoResult;
        this.questionnaire[index].manualVideoPhotoUpload = previous.manualVideoPhotoUpload;
        this.questionnaire[index].uploaded = previous.uploaded;
      }
    });
  }

  getNextQuestionIndex(currentIndex: number): number {
    let nextIndex = currentIndex + 1;
    for (let i = nextIndex; i < this.questionnaire.length; i++) {
      if (!this.questionnaire[i].excluded) {
        break;
      }
      nextIndex++;
    }
    return nextIndex;
  }

  getPreviousQuestionIndex(currentIndex: number): number {
    let previousIndex = currentIndex - 1;
    for (let i = previousIndex; i >= 0; i--) {
      if (!this.questionnaire[i].excluded) {
        break;
      }
      previousIndex--;
    }
    return previousIndex;
  }
}
